import { Space, Table, Tag, Typography } from "antd";
import Icon from "../../masterComponents/Icon";
import { TbReload } from "react-icons/tb";
import { STATUS_COLOR_MAP } from "../../../entities/entities";
import { useState } from "react";
import { useCommonPropertiesContext } from "../../../contexts/commonPropertiesContext";
import ActionButton from "../../masterComponents/actionButton";
import { ContainerWrapper } from "../../../app/app.style";
import {
  CustomTooltip,
  HistoryHead,
  HistorySection,
  NameCell,
  NameCellActions,
  NameCellLabel,
  StatusErrorIcon,
  StatusSpace,
  StatusTagWrap,
  SummaryCard,
} from "./remapping.style";
import { formatDateWithOrdinal } from "../../../utils";
import { useRemapping } from "./useRemapping";
import RemappingTableActions from "./RemappingTableActions";
import RemappingUploadArea from "./remappingUploadArea";
import type { HandleReuseFileType } from "./RemappingType";

const { Title, Text } = Typography;

const PAGE_SIZE = 25;

const Remapping = () => {
  const { currentBatch } = useCommonPropertiesContext();
  const [selectedBatch, setSelectedBatch] = useState(currentBatch);

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [reusedFileName, setReusedFileName] = useState<string | null>(null);
  const [reusedS3Key, setReusedS3Key] = useState<string | null>(null);

  const {
    fetchRemappingUploadResponse,
    remappingUploadResponse,
    isUploading,
    clearWorkflowState,

    remappingJob,
    isJobLoading,
    remappingJobData,

    remappingPreview,
    isRemappingPreviewLoading,
    remappingPreviewUrl,

    fetchRemappingHistory,
    remappingHistoryData,
    page,
    totalRows,
    isHistoryLoading,
    loadMore,
  } = useRemapping({
    selectedBatch,
    selectedFile,
    pageSize: PAGE_SIZE,
  });

  const handleReuseFile: HandleReuseFileType = (record) => {
    clearWorkflowState();
    setSelectedFile(null);
    setReusedFileName(record.file_name);
    setReusedS3Key(record.s3_key);

    const formData = new FormData();
    formData.append("s3_key", record.s3_key);
    fetchRemappingUploadResponse({ batch: selectedBatch }, formData);
  };

  const formatDate = (f) => (
    <span>
      {f.d.format("h:mm A")}
      {" - "}
      {f.d.format("MMM D")}
      <sup>{f.suffix}</sup> {f.d.format("YYYY")}
    </span>
  );

  const columns = [
    {
      title: "Name",
      dataIndex: "file_name",
      render: (_, record) => (
        <NameCell>
          <NameCellActions>
            <RemappingTableActions
              fileS3KeyFile={record.s3_key}
              fetchRemappingUploadResponse={() => handleReuseFile(record)}
            />
          </NameCellActions>
          <NameCellLabel>{record.file_name}</NameCellLabel>
        </NameCell>
      ),
    },
    { title: "Source system", dataIndex: "batch" },
    {
      title: "Started at",
      dataIndex: "started_at",
      render: (_, record) => {
        const formatStart = formatDateWithOrdinal(record.started_at);
        return <span>{formatDate(formatStart)}</span>;
      },
    },
    {
      title: "Finished at",
      dataIndex: "finished_at",
      render: (_, record) => {
        const status = record.status?.toLowerCase();
        if (status === "in_progress" || !record.finished_at) {
          return <span>Ongoing</span>;
        }
        const formatted = formatDateWithOrdinal(record.finished_at);
        if (!formatted?.d?.isValid()) {
          return <span>Ongoing</span>;
        }
        return <span>{formatDate(formatted)}</span>;
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (_, record) => (
        <StatusSpace>
          <StatusTagWrap>
            <Tag
              color={
                STATUS_COLOR_MAP[record.status.toLocaleLowerCase()] || "default"
              }
              style={{
                letterSpacing: "0.1em",
              }}
            >
              {record.status}
            </Tag>
            {record.error_message && (
              <CustomTooltip title={record.error_message} color="#f5f5f5">
                <StatusErrorIcon />
              </CustomTooltip>
            )}
          </StatusTagWrap>
        </StatusSpace>
      ),
    },
    { title: "Author", dataIndex: "author" },
  ];

  return (
    <ContainerWrapper $withoutGap="false">
      <RemappingUploadArea
        selectedBatch={selectedBatch}
        setSelectedBatch={setSelectedBatch}
        reusedFileName={reusedFileName}
        setReusedFileName={setReusedFileName}
        reusedS3Key={reusedS3Key}
        setReusedS3Key={setReusedS3Key}
        selectedFile={selectedFile}
        setSelectedFile={setSelectedFile}
        fetchRemappingUploadResponse={fetchRemappingUploadResponse}
        remappingUploadResponse={remappingUploadResponse}
        isUploading={isUploading}
        clearWorkflowState={clearWorkflowState}
        remappingJob={remappingJob}
        isJobLoading={isJobLoading}
        remappingJobData={remappingJobData}
        remappingPreview={remappingPreview}
        isRemappingPreviewLoading={isRemappingPreviewLoading}
        remappingPreviewUrl={remappingPreviewUrl}
      />

      {remappingUploadResponse?.success && (
        <SummaryCard size="small">
          <Space style={{ flexDirection: "column", alignItems: "flex-start" }}>
            <Title level={5} style={{ margin: 0 }}>
              Details
            </Title>
            <Text>
              {`The remapping process will affect ${remappingUploadResponse?.details?.total_classes} 
              ${remappingUploadResponse?.details?.total_classes === 1 ? "class" : "classes"} 
              (for source system ${selectedBatch})`}
            </Text>
          </Space>

          <ActionButton
            text="Review remapping"
            className="primary-btn-color"
            iconPosition={"end"}
            disabled={true}
          />
        </SummaryCard>
      )}

      <HistorySection>
        <HistoryHead>
          <Title level={5} style={{ margin: 0 }}>
            History
          </Title>
          <ActionButton
            text="Reload"
            className="primary-btn-color"
            onClick={() => fetchRemappingHistory({}, { filters: {} })}
            icon={<Icon icon={TbReload} />}
            iconPosition="end"
            loading={isHistoryLoading}
          />
        </HistoryHead>
        <Table
          className="primary-table"
          rowKey="remapping_sheet_id"
          showSorterTooltip={false}
          tableLayout="auto"
          columns={columns}
          dataSource={remappingHistoryData}
          onChange={(pagination) => {
            loadMore(pagination.current);
          }}
          pagination={{
            current: page,
            pageSize: PAGE_SIZE,
            total: totalRows,
            showSizeChanger: false,
            showQuickJumper: true,
          }}
        />
      </HistorySection>
    </ContainerWrapper>
  );
};

export default Remapping;
